import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatRupiah } from "../lib/format";
import { parseLocaleNum, errMsg } from "../lib/locale";
import { X, Loader2, Printer, Camera, AlertTriangle } from "lucide-react";
import { ScanNotaModal } from "./ScanNotaModal";
import { toast } from "sonner";
import { PreviewNotaModal } from "./PreviewNotaModal";

const today = () => new Date().toISOString().slice(0, 10);

function Modal({ title, onClose, children, maxW = "max-w-2xl", testid }) {
    return (
        <div
            className="fixed inset-0 bg-slate-900/40 z-50 flex md:items-center md:justify-center md:p-4"
            onClick={onClose}
            data-testid={testid}
        >
            <div
                className={`bg-white flex flex-col w-full h-full md:h-auto md:max-h-[90vh] md:rounded-2xl md:shadow-xl md:border md:border-[var(--border)] ${maxW} overflow-hidden`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)] bg-white flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="order-1 md:order-2 text-slate-500 hover:text-slate-900 rounded-full p-1.5 hover:bg-slate-100"
                        aria-label="Tutup"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    <h2 className="order-2 md:order-1 flex-1 font-bold text-slate-900 text-base md:text-sm truncate">{title}</h2>
                </div>
                <div className="p-4 flex-1 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children, required, hint }) {
    return (
        <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
                {hint && <span className="text-slate-400 font-normal"> — {hint}</span>}
            </label>
            {children}
        </div>
    );
}

const inputCls = "w-full border border-[var(--border)] rounded px-3 py-1.5 text-sm bg-white text-[#1D1D1F] placeholder:text-[#A1A1A6] hover:border-[var(--border-strong)] focus:border-[#0071E3]";

// ===========================================================================
// Catat Transaksi (6 jenis) — dropdown jenis + form sesuai
// ===========================================================================

const JENIS_LABEL = {
    pengambilan: "Pengambilan Kain", bahan_penolong: "Bahan Penolong",
    setoran: "Setoran", kasbon: "Kasbon", rijek: "Rijek", setor_perbaikan: "Setor Perbaikan",
};

export function CatatTransaksiModal({ vendor, jenis: initJenis, onClose, onSaved, bons = [], rejects = [], mode = "create", existing = null }) {
    const [jenis, setJenis] = useState(initJenis || "pengambilan");
    const [showScan, setShowScan] = useState(false);
    const [aiData, setAiData] = useState(null);
    const handleExtracted = (d) => { setAiData({ ...d, _ts: Date.now() }); setShowScan(false); };
    if (mode === "edit" && existing) return <EditTransaksiModal existing={existing} onClose={onClose} onSaved={onSaved} />;
    // v1.8.4 defensive guard — AFTER hooks (Rules of Hooks) — never crash if vendor still loading
    if (!vendor || !vendor.id) {
        return (
            <Modal title="Catat Transaksi" onClose={onClose} testid="catat-tx-modal">
                <div className="py-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
                    Memuat data vendor…
                </div>
            </Modal>
        );
    }
    return (
        <Modal title={`Catat Transaksi — ${JENIS_LABEL[jenis]}`} onClose={onClose} testid="catat-tx-modal">
            <div className="mb-3 flex items-end gap-2">
                <div className="flex-1">
                    <Field label="Jenis Transaksi">
                        <select value={jenis} onChange={(e) => { setJenis(e.target.value); setAiData(null); }} data-testid="catat-jenis-switch" className={inputCls}>
                            {Object.entries(JENIS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                    </Field>
                </div>
                <button
                    type="button"
                    onClick={() => setShowScan(true)}
                    data-testid="btn-scan-nota"
                    className="px-3 py-2 border border-indigo-600 text-indigo-700 hover:bg-indigo-50 rounded text-sm font-medium inline-flex items-center gap-1.5 whitespace-nowrap"
                    title="Scan nota dari foto HP pakai AI"
                >
                    <Camera className="w-4 h-4" /> Scan dari Foto
                </button>
            </div>
            {aiData && (
                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800 flex items-center justify-between" data-testid="ai-prefill-banner">
                    <span>✨ Field terisi oleh AI (highlight kuning). Periksa & edit sebelum simpan.</span>
                    <button onClick={() => setAiData(null)} className="text-amber-700 hover:underline font-semibold">Reset</button>
                </div>
            )}
            {jenis === "pengambilan" && <PengambilanForm vendor={vendor} onSaved={onSaved} onClose={onClose} aiData={aiData} />}
            {jenis === "bahan_penolong" && <BahanPenolongForm vendor={vendor} onSaved={onSaved} onClose={onClose} aiData={aiData} />}
            {jenis === "setoran" && <SetoranForm vendor={vendor} bons={bons} onSaved={onSaved} onClose={onClose} aiData={aiData} />}
            {jenis === "kasbon" && <KasbonForm vendor={vendor} onSaved={onSaved} onClose={onClose} aiData={aiData} />}
            {jenis === "rijek" && <RijekForm vendor={vendor} onSaved={onSaved} onClose={onClose} aiData={aiData} />}
            {jenis === "setor_perbaikan" && <SetorPerbaikanForm vendor={vendor} rejects={rejects} onSaved={onSaved} onClose={onClose} aiData={aiData} />}
            <ScanNotaModal open={showScan} jenis={jenis} onClose={() => setShowScan(false)} onExtracted={handleExtracted} />
        </Modal>
    );
}

function PengambilanForm({ vendor, onSaved, onClose, aiData }) {
    const [kains, setKains] = useState([]);
    const [produks, setProduks] = useState([]);
    const [tanggal, setTanggal] = useState(today());
    const [kainNama, setKainNama] = useState("");
    const [kainId, setKainId] = useState("");
    const [jumlah, setJumlah] = useState(10);
    const [satuan, setSatuan] = useState("yard");
    const [harga, setHarga] = useState(10000);
    const [produkDitugaskan, setProdukDitugaskan] = useState([]);
    const [petugas, setPetugas] = useState("MONIKA");
    const [catatan, setCatatan] = useState("");
    const [submit, setSubmit] = useState(false);
    const [aiFields, setAiFields] = useState(new Set());

    useEffect(() => {
        api.get("/master/kain").then((r) => setKains(r.data || []));
        api.get("/master/produk").then((r) => setProduks(r.data || []));
    }, []);

    useEffect(() => {
        if (!aiData) { setAiFields(new Set()); return; }
        const filled = new Set();
        if (aiData.tanggal) { setTanggal(aiData.tanggal); filled.add("tanggal"); }
        if (aiData.petugas) { setPetugas(String(aiData.petugas).toUpperCase()); filled.add("petugas"); }
        if (aiData.nama_kain) {
            setKainNama(aiData.nama_kain);
            const m = kains.find((k) => k.nama_kain?.toLowerCase() === String(aiData.nama_kain).toLowerCase());
            if (m) { setKainId(m.id); setSatuan(m.satuan_default || "yard"); }
            filled.add("kain");
        }
        if (aiData.satuan) { setSatuan(aiData.satuan); filled.add("satuan"); }
        if (aiData.jumlah != null) { setJumlah(parseLocaleNum(aiData.jumlah)); filled.add("jumlah"); }
        if (aiData.harga_per_satuan != null) { setHarga(parseLocaleNum(aiData.harga_per_satuan)); filled.add("harga"); }
        if (aiData.catatan) { setCatatan(aiData.catatan); filled.add("catatan"); }
        if (Array.isArray(aiData.produk_ditugaskan) && aiData.produk_ditugaskan.length > 0) {
            const prodMap = aiData.produk_ditugaskan.map((nm) => {
                const p = produks.find((x) => x.nama_produk?.toLowerCase() === String(nm).toLowerCase());
                return { produk_id: p?.id || "", nama_produk_snapshot: p?.nama_produk || nm };
            }).filter((p) => p.produk_id);
            if (prodMap.length) { setProdukDitugaskan(prodMap); filled.add("produk"); }
        }
        setAiFields(filled);
    }, [aiData, kains.length, produks.length]);

    const aiCls = (f) => aiFields.has(f) ? "bg-amber-50 border-amber-300" : "";

    const addProduk = () => setProdukDitugaskan((p) => [...p, { produk_id: produks[0]?.id || "", nama_produk_snapshot: produks[0]?.nama_produk || "" }]);
    const setProdukRow = (i, pid) => {
        const p = produks.find((x) => x.id === pid);
        setProdukDitugaskan((arr) => arr.map((r, idx) => idx === i ? { produk_id: pid, nama_produk_snapshot: p?.nama_produk || "" } : r));
    };
    const removeProduk = (i) => setProdukDitugaskan((arr) => arr.filter((_, idx) => idx !== i));

    const submitForm = async () => {
        if (!kainNama.trim()) return toast.error("Pilih atau ketik jenis kain");
        if (!harga || parseLocaleNum(harga) <= 0) return toast.error("Harga wajib > 0");
        if (produkDitugaskan.length === 0) return toast.error("Produk ditugaskan WAJIB minimal 1");
        setSubmit(true);
        try {
            const { data } = await api.post("/pengambilan", {
                vendor_id: vendor.id, tanggal,
                items: [{ jenis_kain_id: kainId, nama_kain_snapshot: kainNama,
                          jumlah_roll: 1, yard_per_roll: parseLocaleNum(jumlah),
                          harga_per_yard: parseLocaleNum(harga), satuan_transaksi: satuan }],
                produk_ditugaskan: produkDitugaskan, petugas, catatan,
            });
            toast.success(`Pengambilan ${data.nomor_bon} tersimpan`);
            (data.created_masters || []).forEach((m) => toast.success(`Jenis kain "${m.nama}" ditambahkan ke master`));
            onSaved();
        } catch (e) { console.error(e?.response?.status, e?.response?.data); toast.error(`Gagal: ${errMsg(e)}`, { duration: 8000 }); }
        finally { setSubmit(false); }
    };

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Tanggal"><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={`${inputCls} ${aiCls("tanggal")}`} data-testid="pgm-tanggal" /></Field>
                <Field label="Petugas"><input value={petugas} onChange={(e) => setPetugas(e.target.value)} className={`${inputCls} ${aiCls("petugas")}`} data-testid="pgm-petugas" /></Field>
                <Field label="Jenis Kain" required hint="ketik untuk cari / tambah baru">
                    <input list="kain-list" value={kainNama} onChange={(e) => { setKainNama(e.target.value); const m = kains.find((k) => k.nama_kain === e.target.value); setKainId(m?.id || ""); if (m) { setHarga(m.harga_default_per_yard || m.harga_default_per_satuan || 0); setSatuan(m.satuan_default || "yard"); } }}
                           className={`${inputCls} ${aiCls("kain")}`} data-testid="pgm-kain-nama" />
                    <datalist id="kain-list">{kains.map((k) => <option key={k.id} value={k.nama_kain} />)}</datalist>
                </Field>
                <Field label="Satuan">
                    <select value={satuan} onChange={(e) => setSatuan(e.target.value)} className={`${inputCls} ${aiCls("satuan")}`}>
                        <option value="yard">yard</option><option value="meter">meter</option>
                    </select>
                </Field>
                <Field label={`Jumlah (${satuan})`} required><input type="text" inputMode="decimal" value={jumlah} onChange={(e) => setJumlah(e.target.value)} className={`${inputCls} ${aiCls("jumlah")}`} data-testid="pgm-jumlah" /></Field>
                <Field label={`Harga per ${satuan}`} required><input type="text" inputMode="decimal" value={harga} onChange={(e) => setHarga(e.target.value)} className={`${inputCls} ${aiCls("harga")}`} data-testid="pgm-harga" /></Field>
            </div>

            <div className="border border-dashed border-slate-300 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-slate-700">Produk Ditugaskan <span className="text-red-500">*</span> <span className="text-slate-400 font-normal">(minimal 1)</span></div>
                    <button onClick={addProduk} data-testid="pgm-add-produk" className="text-xs text-teal-600 font-medium hover:underline">+ Tambah Produk</button>
                </div>
                {produkDitugaskan.length === 0 && <div className="text-xs text-slate-400 italic py-2">Belum ada produk. Klik Tambah Produk.</div>}
                {produkDitugaskan.map((p, i) => (
                    <div key={i} className="flex gap-2 mb-1">
                        <select value={p.produk_id} onChange={(e) => setProdukRow(i, e.target.value)} data-testid={`pgm-produk-${i}`} className={`${inputCls} flex-1`}>
                            {produks.map((pr) => <option key={pr.id} value={pr.id}>{pr.nama_produk}</option>)}
                        </select>
                        <button onClick={() => removeProduk(i)} className="text-red-500 text-xs px-2">Hapus</button>
                    </div>
                ))}
            </div>

            <div className="panel p-2 bg-teal-50 text-sm flex justify-between">
                <span>Subtotal</span>
                <span className="font-bold text-teal-700 num-tabular">{formatRupiah(parseLocaleNum(jumlah) * parseLocaleNum(harga))}</span>
            </div>
            <Field label="Catatan"><textarea value={catatan} onChange={(e) => setCatatan(e.target.value)} className={inputCls} rows={2} /></Field>
            <div className="sticky bottom-0 -mx-4 -mb-4 px-4 py-3 bg-white border-t border-slate-200 flex gap-2 justify-end mt-3">
                <button type="button" onClick={onClose} data-testid="pgm-batal" className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium text-sm flex-1 md:flex-initial">Batal</button>
                <button disabled={submit} onClick={submitForm} data-testid="pgm-submit" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm flex-1 md:flex-initial inline-flex items-center justify-center gap-1.5">{submit && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Simpan Pengambilan</button>
            </div>
        </div>
    );
}

function BahanPenolongForm({ vendor, onSaved, onClose, aiData }) {
    const [perals, setPerals] = useState([]);
    const [tanggal, setTanggal] = useState(today());
    const [nama, setNama] = useState("");
    const [peralId, setPeralId] = useState("");
    const [qty, setQty] = useState(1);
    const [harga, setHarga] = useState(0);
    const [petugas, setPetugas] = useState("MONIKA");
    const [submit, setSubmit] = useState(false);
    useEffect(() => { api.get("/master/peralatan").then((r) => setPerals(r.data || [])); }, []);
    useEffect(() => {
        if (!aiData) return;
        if (aiData.tanggal) setTanggal(aiData.tanggal);
        if (aiData.petugas) setPetugas(String(aiData.petugas).toUpperCase());
        if (aiData.nama_item) { setNama(aiData.nama_item); const m = perals.find((p) => p.nama_item?.toLowerCase() === String(aiData.nama_item).toLowerCase()); if (m) setPeralId(m.id); }
        if (aiData.qty != null) setQty(parseLocaleNum(aiData.qty));
        if (aiData.harga_per_satuan != null) setHarga(parseLocaleNum(aiData.harga_per_satuan));
    }, [aiData, perals.length]);
    const submitForm = async () => {
        if (!nama.trim()) return toast.error("Nama peralatan wajib");
        if (!harga || parseLocaleNum(harga) <= 0) return toast.error("Harga wajib > 0");
        setSubmit(true);
        try {
            const { data } = await api.post("/bon-peralatan-v2", {
                vendor_id: vendor.id, tanggal,
                items: [{ peralatan_id: peralId, nama_item_snapshot: nama, qty: parseLocaleNum(qty), harga_snapshot: parseLocaleNum(harga) }],
                petugas,
            });
            toast.success(`Bon Peralatan ${data.nomor_bon} tersimpan`);
            (data.created_masters || []).forEach((m) => toast.success(`"${m.nama}" ditambahkan ke master`));
            onSaved();
        } catch (e) { console.error(e?.response?.status, e?.response?.data); toast.error(`Gagal: ${errMsg(e)}`, { duration: 8000 }); }
        finally { setSubmit(false); }
    };
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Tanggal"><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} /></Field>
                <Field label="Petugas"><input value={petugas} onChange={(e) => setPetugas(e.target.value)} className={inputCls} /></Field>
                <Field label="Nama Item" required hint="ketik untuk cari / tambah baru">
                    <input list="per-list" value={nama} onChange={(e) => { setNama(e.target.value); const m = perals.find((p) => p.nama_item === e.target.value); setPeralId(m?.id || ""); if (m) setHarga(m.harga || 0); }} className={inputCls} data-testid="bp-nama" />
                    <datalist id="per-list">{perals.map((p) => <option key={p.id} value={p.nama_item} />)}</datalist>
                </Field>
                <Field label="Qty" required><input type="text" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} /></Field>
                <Field label="Harga per Satuan" required><input type="text" inputMode="decimal" value={harga} onChange={(e) => setHarga(e.target.value)} className={inputCls} data-testid="bp-harga" /></Field>
            </div>
            <div className="panel p-2 bg-teal-50 text-sm flex justify-between"><span>Subtotal</span><span className="font-bold text-teal-700 num-tabular">{formatRupiah(qty * harga)}</span></div>
            <div className="sticky bottom-0 -mx-4 -mb-4 px-4 py-3 bg-white border-t border-slate-200 flex gap-2 justify-end mt-3">
                <button type="button" onClick={onClose} data-testid="bp-batal" className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium text-sm flex-1 md:flex-initial">Batal</button>
                <button disabled={submit} onClick={submitForm} data-testid="bp-submit" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm flex-1 md:flex-initial">Simpan</button>
            </div>
        </div>
    );
}

function SetoranForm({ vendor, bons, onSaved, onClose, aiData }) {
    const [produks, setProduks] = useState([]);
    const [tanggal, setTanggal] = useState(today());
    const [items, setItems] = useState([{ bon_kain_id: "", produk_id: "", nama_produk_snapshot: "", jumlah_pcs: 1, harga_per_pcs_snapshot: 0 }]);
    const [petugas, setPetugas] = useState("MONIKA");
    const [submit, setSubmit] = useState(false);
    useEffect(() => { api.get("/master/produk").then((r) => setProduks(r.data || [])); }, []);
    useEffect(() => {
        if (!aiData) return;
        if (aiData.tanggal) setTanggal(aiData.tanggal);
        if (aiData.petugas) setPetugas(String(aiData.petugas).toUpperCase());
        if (aiData.nama_produk || aiData.jumlah_pcs != null || aiData.harga_per_pcs != null || aiData.bon_kain_asal) {
            const bon = aiData.bon_kain_asal ? bons.find((b) => b.nomor_bon === aiData.bon_kain_asal) : null;
            const prod = aiData.nama_produk ? produks.find((p) => p.nama_produk?.toLowerCase() === String(aiData.nama_produk).toLowerCase()) : null;
            setItems([{
                bon_kain_id: bon?.id || "",
                produk_id: prod?.id || "",
                nama_produk_snapshot: prod?.nama_produk || aiData.nama_produk || "",
                jumlah_pcs: parseLocaleNum(aiData.jumlah_pcs) || 1,
                harga_per_pcs_snapshot: parseLocaleNum(aiData.harga_per_pcs) || prod?.harga_default || 0,
            }]);
        }
    }, [aiData, produks.length, bons.length]);
    const bonKains = bons.filter((b) => b._jenis === "bon_kain" && (b.sisa_bon || 0) > 0.01);

    const setItem = (i, patch) => setItems((arr) => arr.map((r, idx) => idx === i ? { ...r, ...patch } : r));
    const addRow = () => setItems((a) => [...a, { bon_kain_id: "", produk_id: "", nama_produk_snapshot: "", jumlah_pcs: 1, harga_per_pcs_snapshot: 0 }]);
    const total = items.reduce((s, r) => s + parseLocaleNum(r.jumlah_pcs) * parseLocaleNum(r.harga_per_pcs_snapshot), 0);

    const submitForm = async () => {
        for (const r of items) {
            if (!r.bon_kain_id || !r.produk_id || !parseLocaleNum(r.jumlah_pcs) || !parseLocaleNum(r.harga_per_pcs_snapshot)) return toast.error("Lengkapi semua baris alokasi");
        }
        setSubmit(true);
        try {
            const resp = await api.post("/setor-barang-v2", {
                vendor_id: vendor.id, tanggal_setor: tanggal,
                items: items.map((r) => ({ ...r, jumlah_pcs: parseLocaleNum(r.jumlah_pcs), harga_per_pcs_snapshot: parseLocaleNum(r.harga_per_pcs_snapshot) })),
                petugas,
            });
            const data = resp?.data || {};
            try {
                toast.success(`Setoran ${data.nomor_setor || "baru"} tersimpan`);
                if (data?.tanggal_setor) {
                    toast.info(`Lihat di jurnal — tanggal ${data.tanggal_setor}`);
                }
            } catch (toastErr) { /* ignore */ }
            onSaved();
        } catch (e) {
            console.error("[Setor save] error:", e?.response?.status, e?.response?.data);
            const d = e?.response?.data;
            let msg;
            if (typeof d?.detail === "string") msg = d.detail;
            else if (Array.isArray(d?.detail)) msg = d.detail.map((x) => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join("; ");
            else if (typeof d?.message === "string") msg = d.message;
            else msg = e?.message || "Gagal menyimpan setoran";
            toast.error(`Gagal: ${msg}`, { duration: 10000 });
        } finally { setSubmit(false); }
    };
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Tanggal"><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} /></Field>
                <Field label="Petugas"><input value={petugas} onChange={(e) => setPetugas(e.target.value)} className={inputCls} /></Field>
            </div>
            <div className="border border-dashed border-slate-300 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-slate-700">Alokasi ke Pengambilan</div>
                    <button onClick={addRow} className="text-xs text-teal-600 font-medium hover:underline">+ Tambah Alokasi</button>
                </div>
                {bonKains.length === 0 && <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">Belum ada pengambilan terbuka. Buat Pengambilan Kain dulu.</div>}
                {items.map((r, i) => {
                    const p = produks.find((x) => x.id === r.produk_id);
                    return (
                        <div key={i} className="grid grid-cols-6 gap-2 mb-2">
                            <select value={r.bon_kain_id} onChange={(e) => setItem(i, { bon_kain_id: e.target.value })} data-testid={`setor-bon-${i}`} className={`${inputCls} col-span-2`}>
                                <option value="">Pilih Pengambilan</option>
                                {bonKains.map((b) => <option key={b.id} value={b.id}>{b.nomor_bon} (sisa {formatRupiah(b.sisa_bon)})</option>)}
                            </select>
                            <select value={r.produk_id} onChange={(e) => { const pp = produks.find((x) => x.id === e.target.value); setItem(i, { produk_id: e.target.value, nama_produk_snapshot: pp?.nama_produk || "", harga_per_pcs_snapshot: pp?.harga_default || 0 }); }} data-testid={`setor-produk-${i}`} className={inputCls}>
                                <option value="">Produk</option>
                                {produks.map((pr) => <option key={pr.id} value={pr.id}>{pr.nama_produk}</option>)}
                            </select>
                            <input type="text" inputMode="decimal" placeholder="pcs (desimal OK, mis. 1790,3)" value={r.jumlah_pcs} onChange={(e) => setItem(i, { jumlah_pcs: e.target.value })} className={inputCls} data-testid={`setor-pcs-${i}`} />
                            <input type="text" inputMode="decimal" placeholder="harga/pcs" value={r.harga_per_pcs_snapshot} onChange={(e) => setItem(i, { harga_per_pcs_snapshot: e.target.value })} className={inputCls} data-testid={`setor-harga-${i}`} />
                            <div className="text-right num-tabular text-sm pt-1.5">{formatRupiah(parseLocaleNum(r.jumlah_pcs) * parseLocaleNum(r.harga_per_pcs_snapshot))}</div>
                        </div>
                    );
                })}
            </div>
            <div className="panel p-2 bg-teal-50 text-sm flex justify-between"><span>Total Setoran</span><span className="font-bold text-teal-700 num-tabular">{formatRupiah(total)}</span></div>
            <div className="sticky bottom-0 -mx-4 -mb-4 px-4 py-3 bg-white border-t border-slate-200 flex gap-2 justify-end mt-3">
                <button type="button" onClick={onClose} data-testid="setor-batal" className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium text-sm flex-1 md:flex-initial">Batal</button>
                <button disabled={submit} onClick={submitForm} data-testid="setor-submit" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm flex-1 md:flex-initial">Simpan Setoran</button>
            </div>
        </div>
    );
}

function KasbonForm({ vendor, onSaved, onClose, aiData }) {
    const [plafon, setPlafon] = useState(null);
    const [bons, setBons] = useState([]);
    const [tanggal, setTanggal] = useState(today());
    const [jumlah, setJumlah] = useState(0);
    const [alasan, setAlasan] = useState("");
    const [petugas, setPetugas] = useState("MONIKA");
    const [alokasi, setAlokasi] = useState([]); // [{bon_kain_id, nominal}]
    const [submit, setSubmit] = useState(false);
    const [totalans, setTotalans] = useState([]);
    useEffect(() => {
        if (!vendor?.id) return;
        api.get(`/vendors/${vendor.id}/plafon-v2`).then((r) => setPlafon(r.data)).catch(() => {});
        api.get(`/bon-kain?vendor_id=${vendor.id}`).then((r) => setBons(r.data || [])).catch(() => {});
        api.get(`/totalan?vendor_id=${vendor.id}`).then((r) => setTotalans(r.data || [])).catch(() => {});
    }, [vendor?.id]);
    useEffect(() => {
        if (!aiData) return;
        if (aiData.tanggal) setTanggal(aiData.tanggal);
        if (aiData.petugas) setPetugas(String(aiData.petugas).toUpperCase());
        if (aiData.jumlah != null) setJumlah(parseLocaleNum(aiData.jumlah));
        if (aiData.alasan) setAlasan(aiData.alasan);
    }, [aiData]);

    const sisaBonById = (bid) => {
        const b = bons.find((x) => x.id === bid);
        if (!b) return 0;
        let pot = 0;
        for (const t of totalans) for (const a of (t.alokasi_potongan_bon || [])) if (a.bon_id === bid) pot += parseLocaleNum(a.nilai_potongan || 0);
        return (b.total_nilai || 0) - pot;
    };
    const bonsTerbuka = bons.filter((b) => sisaBonById(b.id) > 0.01);

    const addAlokasi = () => setAlokasi((a) => [...a, { bon_kain_id: bonsTerbuka[0]?.id || "", nominal: 0 }]);
    const setAlokasiRow = (i, patch) => setAlokasi((arr) => arr.map((r, idx) => idx === i ? { ...r, ...patch } : r));
    const removeAlokasi = (i) => setAlokasi((arr) => arr.filter((_, idx) => idx !== i));
    const sumAlok = alokasi.reduce((s, a) => s + parseLocaleNum(a.nominal || 0), 0);
    const overAlok = sumAlok > parseLocaleNum(jumlah || 0);

    const over = plafon && parseLocaleNum(jumlah) > plafon.plafon + 0.01;
    const submitForm = async () => {
        if (!jumlah || parseLocaleNum(jumlah) <= 0) return toast.error("Jumlah > 0");
        if (overAlok) return toast.error("Total alokasi > jumlah kasbon");
        for (const a of alokasi) {
            if (!a.bon_kain_id || parseLocaleNum(a.nominal) <= 0) return toast.error("Lengkapi semua baris alokasi atau hapus");
            if (parseLocaleNum(a.nominal) > sisaBonById(a.bon_kain_id) + 0.01) return toast.error(`Alokasi melebihi sisa bon pengambilan`);
        }
        setSubmit(true);
        try {
            const key = crypto.randomUUID();
            const { data } = await api.post("/kasbon-v2", {
                vendor_id: vendor.id, tanggal, jumlah_kasbon: parseLocaleNum(jumlah),
                alasan, petugas, idempotency_key: key,
                alokasi_pengambilan: alokasi.filter((a) => a.bon_kain_id && parseLocaleNum(a.nominal) > 0).map((a) => ({ bon_kain_id: a.bon_kain_id, nominal: parseLocaleNum(a.nominal) })),
            });
            toast.success(`Kasbon ${data.nomor_kasbon} tersimpan`);
            if (data.warning) toast.warning(data.warning, { duration: 8000 });
            onSaved();
        } catch (e) { console.error(e?.response?.status, e?.response?.data); toast.error(`Gagal: ${errMsg(e)}`, { duration: 8000 }); }
        finally { setSubmit(false); }
    };
    return (
        <div className="space-y-3">
            <div className="panel p-3 bg-amber-50 border-amber-200 text-xs">
                <div className="font-bold mb-1">Batas Kasbon Global</div>
                <div className="num-tabular text-amber-700 font-bold text-lg">{plafon ? formatRupiah(Math.max(0, plafon.plafon)) : "..."}</div>
                <div className="text-slate-500 mt-1">Kredit: {formatRupiah(plafon?.kredit_valid)} − Bon: {formatRupiah(plafon?.total_bon_asli)} − Kasbon: {formatRupiah(plafon?.total_kasbon_asli)} − Dibayar: {formatRupiah(plafon?.total_pembayaran)}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Tanggal"><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} /></Field>
                <Field label="Petugas"><input value={petugas} onChange={(e) => setPetugas(e.target.value)} className={inputCls} /></Field>
                <Field label="Jumlah Kasbon" required><input type="text" inputMode="decimal" value={jumlah} onChange={(e) => setJumlah(e.target.value)} className={inputCls} data-testid="kasbon-jumlah" /></Field>
                <Field label="Alasan"><input value={alasan} onChange={(e) => setAlasan(e.target.value)} className={inputCls} /></Field>
            </div>

            <div className="border border-dashed border-slate-300 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-slate-700">Alokasi ke Pengambilan <span className="text-slate-400 font-normal">(opsional)</span></div>
                    <button onClick={addAlokasi} data-testid="kasbon-add-alokasi" disabled={bonsTerbuka.length === 0} className="text-xs text-teal-600 font-medium hover:underline disabled:text-slate-400">+ Tambah Alokasi</button>
                </div>
                {bonsTerbuka.length === 0 && alokasi.length === 0 && <div className="text-xs text-slate-400 italic py-1">Tidak ada pengambilan terbuka. Kasbon hanya dibatasi plafon global.</div>}
                {alokasi.map((a, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 mb-2">
                        <select value={a.bon_kain_id} onChange={(e) => setAlokasiRow(i, { bon_kain_id: e.target.value })} data-testid={`kasbon-alok-bon-${i}`} className={`${inputCls} col-span-3`}>
                            {bonsTerbuka.map((b) => <option key={b.id} value={b.id}>{b.nomor_bon} (sisa {formatRupiah(sisaBonById(b.id))})</option>)}
                        </select>
                        <input type="text" inputMode="decimal" value={a.nominal} onChange={(e) => setAlokasiRow(i, { nominal: e.target.value })} data-testid={`kasbon-alok-nom-${i}`} className={inputCls} />
                        <button onClick={() => removeAlokasi(i)} className="text-red-500 text-xs px-2">Hapus</button>
                    </div>
                ))}
                {alokasi.length > 0 && (
                    <div className={`text-xs mt-1 ${overAlok ? "text-red-600" : "text-slate-500"}`}>Total alokasi: {formatRupiah(sumAlok)} / Jumlah kasbon: {formatRupiah(jumlah)}</div>
                )}
            </div>

            {over && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="kasbon-over-plafon-warning">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>Jumlah melebihi plafon global <b>{formatRupiah(Math.max(0, plafon.plafon))}</b>. Vendor akan punya kasbon lebih dari hak (Hak Vendor jadi negatif). Tetap bisa disimpan — pastikan sudah benar.</div>
                </div>
            )}
            <div className="sticky bottom-0 -mx-4 -mb-4 px-4 py-3 bg-white border-t border-slate-200 flex gap-2 justify-end mt-3">
                <button type="button" onClick={onClose} data-testid="kasbon-batal" className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium text-sm flex-1 md:flex-initial">Batal</button>
                <button disabled={submit || overAlok} onClick={submitForm} data-testid="kasbon-submit" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm flex-1 md:flex-initial disabled:bg-slate-300">Simpan Kasbon</button>
            </div>
        </div>
    );
}

const SATUAN_LBL = {
    pcs: { jumlah: "Jumlah Pcs", harga: "Harga Manual per Pcs", ph: "pcs (bulat, mis. 5)", short: "pcs", unit: "pcs" },
    meter: { jumlah: "Jumlah Meter", harga: "Harga Manual per Meter", ph: "meter (desimal OK, mis. 52,2 atau 52.2)", short: "mtr", unit: "meter" },
};
const detectSatuan = (nama) => (/kain|fabric/i.test(nama || "") ? "meter" : "pcs");
export const fmtQty = (n) => Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

function SatuanToggle({ value, onChange }) {
    return (
        <div className="inline-flex rounded-full p-1 bg-[#F5F5F7]" data-testid="rijek-satuan">
            {[["pcs", "Barang (pcs)"], ["meter", "Kain (meter)"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => onChange(k)} data-testid={`rijek-satuan-${k}`}
                    className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${value === k ? "bg-white text-[#0071E3] shadow-sm" : "text-slate-600"}`}>
                    {l}
                </button>
            ))}
        </div>
    );
}

function RijekEditModal({ existing, onClose, onSaved }) {
    const [doc, setDoc] = useState(null);
    const [tanggal, setTanggal] = useState("");
    const [satuan, setSatuan] = useState("pcs");
    const [jumlah, setJumlah] = useState("");
    const [harga, setHarga] = useState("");
    const [alasan, setAlasan] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get(`/reject/${existing.id}`).then(({ data }) => {
            setDoc(data); setTanggal(data.tanggal || ""); setSatuan(data.satuan || "pcs");
            setJumlah(String(data.jumlah_pcs ?? "").replace(".", ","));
            setHarga(String(data.harga_pcs_manual || "").replace(".", ",")); setAlasan(data.alasan || "");
        }).catch((e) => { toast.error(errMsg(e, "Gagal memuat rijek")); onClose(); });
        // eslint-disable-next-line
    }, [existing.id]);
    const isB = doc?.jenis === "B_tidak_bisa_diperbaiki";
    const hargaEf = isB ? parseLocaleNum(harga) : Number(doc?.harga_pcs_setor_asli || 0);
    const qty = parseLocaleNum(jumlah);
    const submit = async () => {
        if (qty <= 0) return toast.error("Jumlah harus > 0");
        if (satuan === "pcs" && !Number.isInteger(qty)) return toast.error("Jumlah pcs harus bulat. Pilih 'Kain (meter)' untuk desimal.");
        if (isB && hargaEf <= 0) return toast.error("Harga manual wajib > 0");
        setSaving(true);
        try {
            await api.put(`/reject/${doc.id}`, { tanggal, satuan, jumlah_pcs: qty, alasan, ...(isB ? { harga_pcs_manual: hargaEf } : {}) });
            toast.success(`Rijek ${doc.nomor_reject} berhasil diperbarui`);
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Gagal menyimpan perubahan")); }
        finally { setSaving(false); }
    };
    return (
        <Modal title={`Edit Rijek ${isB ? "B" : "A"}: ${doc?.nomor_reject || existing.nomor || ""}`} onClose={onClose} testid="edit-tx-modal">
            {!doc ? <div className="py-8 text-center text-sm text-slate-400">Memuat rijek…</div> : (
                <div className="space-y-3">
                    <div className="text-xs text-slate-500">{doc.nama_produk_snapshot} · dari {doc.setor_nomor_snapshot} · {isB ? "B — Buyback" : "A — Bisa Diperbaiki"}</div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Tanggal" required><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} data-testid="edit-tanggal" className={inputCls} /></Field>
                        <Field label="Jenis Barang" required><SatuanToggle value={satuan} onChange={setSatuan} /></Field>
                        <Field label={SATUAN_LBL[satuan].jumlah} required>
                            <input type="text" inputMode="decimal" value={jumlah} onChange={(e) => setJumlah(e.target.value)} placeholder={SATUAN_LBL[satuan].ph} data-testid="edit-rijek-jumlah" className={inputCls} />
                        </Field>
                        {isB ? (
                            <Field label={SATUAN_LBL[satuan].harga} required>
                                <input type="text" inputMode="decimal" value={harga} onChange={(e) => setHarga(e.target.value)} data-testid="edit-rijek-harga" className={inputCls} />
                            </Field>
                        ) : (
                            <Field label={`Harga Setor per ${SATUAN_LBL[satuan].unit}`}><input readOnly value={formatRupiah(hargaEf)} className={`${inputCls} bg-slate-50 text-slate-500`} /></Field>
                        )}
                    </div>
                    <Field label="Alasan"><textarea value={alasan} onChange={(e) => setAlasan(e.target.value)} rows={2} data-testid="edit-catatan" className={inputCls} /></Field>
                    <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
                        <div className="text-sm" data-testid="edit-total">{fmtQty(qty)} {SATUAN_LBL[satuan].short} × {formatRupiah(hargaEf)} = <b>{formatRupiah(qty * hargaEf)}</b></div>
                        <div className="flex gap-2">
                            <button onClick={onClose} data-testid="edit-cancel" className="px-3 py-1.5 border border-slate-300 rounded text-sm">Batal</button>
                            <button onClick={submit} disabled={saving} data-testid="edit-submit" className="px-4 py-1.5 rounded text-sm font-medium text-white bg-[#0071E3] hover:bg-[#0077ED] disabled:opacity-50">Simpan Perubahan</button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}

function RijekForm({ vendor, onSaved, onClose, aiData }) {
    const [setors, setSetors] = useState([]);
    const [setorId, setSetorId] = useState("");
    const [itemIdx, setItemIdx] = useState(0);
    const [jenisR, setJenisR] = useState("A_bisa_diperbaiki");
    const [pcs, setPcs] = useState(1);
    const [hargaManual, setHargaManual] = useState("");
    const [alasan, setAlasan] = useState("");
    const [tanggal, setTanggal] = useState(today());
    const [petugas, setPetugas] = useState("MONIKA");
    const [submit, setSubmit] = useState(false);
    useEffect(() => { if (!vendor?.id) return; api.get(`/setor-barang?vendor_id=${vendor.id}`).then((r) => setSetors(r.data || [])).catch(() => {}); }, [vendor?.id]);
    useEffect(() => {
        if (!aiData) return;
        if (aiData.tanggal) setTanggal(aiData.tanggal);
        if (aiData.petugas) setPetugas(String(aiData.petugas).toUpperCase());
        if (aiData.jumlah_pcs != null) setPcs(parseLocaleNum(aiData.jumlah_pcs));
        if (aiData.jenis === "A") setJenisR("A_bisa_diperbaiki");
        else if (aiData.jenis === "B") setJenisR("B_tidak_bisa_diperbaiki");
        if (aiData.harga_manual != null) setHargaManual(String(aiData.harga_manual));
        if (aiData.alasan) setAlasan(aiData.alasan);
        if (aiData.setor_asal) {
            const s = setors.find((x) => x.nomor_setor === aiData.setor_asal);
            if (s) setSetorId(s.id);
        }
    }, [aiData, setors.length]);
    const setor = setors.find((s) => s.id === setorId);
    const items = setor?.items || [];
    const [satuan, setSatuan] = useState("pcs");
    const [satuanTouched, setSatuanTouched] = useState(false);
    const itemNama = items[Number(itemIdx)]?.nama_produk_snapshot || "";
    useEffect(() => { if (!satuanTouched) setSatuan(detectSatuan(itemNama)); }, [itemNama, satuanTouched]);
    const hargaPreview = jenisR === "B_tidak_bisa_diperbaiki" ? parseLocaleNum(hargaManual) : Number(items[Number(itemIdx)]?.harga_per_pcs_snapshot || 0);
    const submitForm = async () => {
        if (!setorId) return toast.error("Pilih setor");
        const qty = parseLocaleNum(pcs);
        if (qty <= 0) return toast.error("Jumlah harus > 0");
        if (satuan === "pcs" && !Number.isInteger(qty)) return toast.error("Jumlah pcs harus bulat. Pilih 'Kain (meter)' untuk desimal.");
        if (jenisR === "B_tidak_bisa_diperbaiki" && (!hargaManual || parseLocaleNum(hargaManual) <= 0)) {
            return toast.error(`Harga manual per ${SATUAN_LBL[satuan].unit} WAJIB (> 0) untuk reject B`);
        }
        setSubmit(true);
        try {
            const { data } = await api.post("/reject", {
                setor_id: setorId, item_index: parseLocaleNum(itemIdx), jenis: jenisR, satuan,
                jumlah_pcs: qty, harga_pcs_manual: hargaManual ? parseLocaleNum(hargaManual) : null,
                alasan, tanggal, petugas,
            });
            toast.success(`Rijek ${data.nomor_reject} dicatat`);
            if (data.bon_baru_id) toast.success("Bon baru buyback dibuat");
            onSaved();
        } catch (e) { console.error(e?.response?.status, e?.response?.data); toast.error(`Gagal: ${errMsg(e)}`, { duration: 8000 }); }
        finally { setSubmit(false); }
    };
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <Field label="Tanggal"><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} /></Field>
                <Field label="Petugas"><input value={petugas} onChange={(e) => setPetugas(e.target.value)} className={inputCls} /></Field>
                <Field label="Setor Asal" required>
                    <select value={setorId} onChange={(e) => { setSetorId(e.target.value); setItemIdx(0); }} data-testid="rijek-setor" className={inputCls}>
                        <option value="">Pilih setor</option>
                        {setors.map((s) => <option key={s.id} value={s.id}>{s.nomor_setor} ({s.tanggal_setor})</option>)}
                    </select>
                </Field>
                <Field label="Produk (baris)" required>
                    <select value={itemIdx} onChange={(e) => setItemIdx(e.target.value)} data-testid="rijek-item" className={inputCls}>
                        {items.map((it, i) => <option key={i} value={i}>{it.nama_produk_snapshot} ({it.jumlah_pcs} pcs)</option>)}
                    </select>
                </Field>
                <Field label="Jenis Reject" required>
                    <select value={jenisR} onChange={(e) => setJenisR(e.target.value)} data-testid="rijek-jenis" className={inputCls}>
                        <option value="A_bisa_diperbaiki">A — Bisa Diperbaiki</option>
                        <option value="B_tidak_bisa_diperbaiki">B — Tidak Bisa (Buyback)</option>
                    </select>
                </Field>
                <Field label="Jenis Barang" required>
                    <SatuanToggle value={satuan} onChange={(v) => { setSatuan(v); setSatuanTouched(true); }} />
                </Field>
                <Field label={SATUAN_LBL[satuan].jumlah} required><input type="text" inputMode="decimal" value={pcs} onChange={(e) => setPcs(e.target.value)} placeholder={SATUAN_LBL[satuan].ph} className={inputCls} data-testid="rijek-pcs" /></Field>
                {jenisR === "B_tidak_bisa_diperbaiki" && (
                    <Field label={SATUAN_LBL[satuan].harga} required hint="WAJIB diisi, tidak boleh 0">
                        <input type="text" inputMode="decimal" value={hargaManual} onChange={(e) => setHargaManual(e.target.value)} placeholder="(kosong — isi manual)" className={inputCls} data-testid="rijek-harga-manual" />
                    </Field>
                )}
            </div>
            <div className="text-xs text-slate-600" data-testid="rijek-preview">
                {fmtQty(parseLocaleNum(pcs))} {SATUAN_LBL[satuan].short} × {formatRupiah(hargaPreview)} = <b>{formatRupiah(parseLocaleNum(pcs) * hargaPreview)}</b>
            </div>
            <Field label="Alasan" required><textarea value={alasan} onChange={(e) => setAlasan(e.target.value)} rows={2} className={inputCls} data-testid="rijek-alasan" /></Field>
            <div className="sticky bottom-0 -mx-4 -mb-4 px-4 py-3 bg-white border-t border-slate-200 flex gap-2 justify-end mt-3">
                <button type="button" onClick={onClose} data-testid="rijek-batal" className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium text-sm flex-1 md:flex-initial">Batal</button>
                <button disabled={submit} onClick={submitForm} data-testid="rijek-submit" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm flex-1 md:flex-initial">Simpan Rijek</button>
            </div>
        </div>
    );
}

function SetorPerbaikanForm({ vendor, rejects, onSaved, onClose, aiData }) {
    const [rejectId, setRejectId] = useState(rejects[0]?.id || "");
    const [pcs, setPcs] = useState(1);
    const [tanggal, setTanggal] = useState(today());
    const [petugas, setPetugas] = useState("MONIKA");
    const [submit, setSubmit] = useState(false);
    const [produk, setProduk] = useState(null);
    const r = rejects.find((x) => x.id === rejectId);
    useEffect(() => { if (r?.produk_id) api.get("/master/produk").then((rs) => setProduk(rs.data.find((p) => p.id === r.produk_id))); }, [rejectId]);
    useEffect(() => {
        if (!aiData) return;
        if (aiData.tanggal) setTanggal(aiData.tanggal);
        if (aiData.petugas) setPetugas(String(aiData.petugas).toUpperCase());
        if (aiData.jumlah_pcs != null) setPcs(parseLocaleNum(aiData.jumlah_pcs));
        if (aiData.reject_asal) {
            const rr = rejects.find((x) => x.nomor_reject === aiData.reject_asal);
            if (rr) setRejectId(rr.id);
        }
    }, [aiData, rejects.length]);
    const kreditEstimasi = produk ? parseLocaleNum(pcs) * parseLocaleNum(produk.harga_default || 0) : 0;
    const submitForm = async () => {
        if (!rejectId) return toast.error("Pilih reject");
        setSubmit(true);
        try {
            const { data } = await api.post("/setor-perbaikan", {
                reject_id: rejectId, jumlah_pcs: parseLocaleNum(pcs), tanggal, petugas,
            });
            toast.success(`Setor perbaikan ${data.nomor_sp} tersimpan, kredit ${formatRupiah(data.nilai_kredit)}`);
            onSaved();
        } catch (e) { console.error(e?.response?.status, e?.response?.data); toast.error(`Gagal: ${errMsg(e)}`, { duration: 8000 }); }
        finally { setSubmit(false); }
    };
    return (
        <div className="space-y-3">
            {rejects.length === 0 && <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded">Tidak ada reject A terbuka. Catat reject A dulu.</div>}
            <div className="grid grid-cols-2 gap-3">
                <Field label="Tanggal"><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} /></Field>
                <Field label="Petugas"><input value={petugas} onChange={(e) => setPetugas(e.target.value)} className={inputCls} /></Field>
                <Field label="Reject A" required>
                    <select value={rejectId} onChange={(e) => setRejectId(e.target.value)} data-testid="sp-reject" className={inputCls}>
                        <option value="">Pilih reject</option>
                        {rejects.map((rr) => <option key={rr.id} value={rr.id}>{rr.nomor_reject} — {rr.nama_produk_snapshot} ({rr.sisa_pcs} pcs sisa)</option>)}
                    </select>
                </Field>
                <Field label="Jumlah Pcs" required><input type="text" inputMode="decimal" value={pcs} onChange={(e) => setPcs(e.target.value)} className={inputCls} data-testid="sp-pcs" /></Field>
            </div>
            {produk && (
                <div className="panel p-2 bg-teal-50 text-sm">
                    <div className="flex justify-between"><span>Harga master {produk.nama_produk} (snapshot)</span><span className="num-tabular font-medium">{formatRupiah(produk.harga_default)}</span></div>
                    <div className="flex justify-between"><span>Kredit yang akan dicatat</span><span className="font-bold text-teal-700 num-tabular">{formatRupiah(kreditEstimasi)}</span></div>
                </div>
            )}
            <div className="sticky bottom-0 -mx-4 -mb-4 px-4 py-3 bg-white border-t border-slate-200 flex gap-2 justify-end mt-3">
                <button type="button" onClick={onClose} data-testid="sp-batal" className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium text-sm flex-1 md:flex-initial">Batal</button>
                <button disabled={submit} onClick={submitForm} data-testid="sp-submit" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm flex-1 md:flex-initial">Simpan Setor Perbaikan</button>
            </div>
        </div>
    );
}

// ===========================================================================
// Totalan Modal — pisah dari Catat Transaksi
// ===========================================================================

export function TotalanModal({ vendor, onClose, onSaved }) {
    // v1.8.33 — sisa dari backend (helper tunggal, hanya Totalan aktif)
    const [open, setOpen] = useState({ setor_items: [], kasbons: [], bons: [] });
    const [potManual, setPotManual] = useState(false);
    const todayStr = today();
    const defaultDari = (() => { const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10); })();
    const [dari, setDari] = useState(defaultDari);
    const [sampai, setSampai] = useState(todayStr);
    const [tanggal, setTanggal] = useState(today());
    const [petugas, setPetugas] = useState("MONIKA");
    const [alokasiSetor, setAlokasiSetor] = useState({});   // {setor_id|idx: {pcs, nilai}}
    const [alokasiBon, setAlokasiBon] = useState({});       // {bon_id: nilai}
    const [alokasiKasbon, setAlokasiKasbon] = useState({});
    const [pembayaran, setPembayaran] = useState(0);
    const [submit, setSubmit] = useState(false);
    const [preview, setPreview] = useState(null);
    const [ttDibuat, setTtDibuat] = useState("MONIKA");
    const [ttDiperiksa, setTtDiperiksa] = useState("YUNITA");
    const [ttDisetujui, setTtDisetujui] = useState("RAJAYA");
    useEffect(() => {
        if (!vendor?.id) return;
        api.get(`/vendors/${vendor.id}/totalan/sisa`)
            .then((r) => setOpen(r.data || { setor_items: [], kasbons: [], bons: [] }))
            .catch((e) => toast.error(`Gagal memuat sisa: ${errMsg(e)}`));
    }, [vendor?.id]);

    const setorsFiltered = open.setor_items.filter((s) => (s.tanggal_setor || "") >= dari && (s.tanggal_setor || "") <= sampai);

    const sumSetor = Object.values(alokasiSetor).reduce((s, v) => s + parseLocaleNum(v?.nilai || 0), 0);

    // v1.8.33 — Potong Rp otomatis: Kasbon (terlama) → Rijek B (terlama) → Bon Kain/Peralatan FIFO (urutan dari backend)
    useEffect(() => {
        if (potManual) return;
        let budget = Math.round(sumSetor * 100) / 100;
        const take = (list) => {
            const out = {};
            for (const x of list) {
                if (budget <= 0.01) break;
                const p = Math.round(Math.min(x.sisa, budget) * 100) / 100;
                out[x.id] = p; budget -= p;
            }
            return out;
        };
        const kas = take(open.kasbons);
        const bon = take(open.bons);
        setAlokasiKasbon(kas);
        setAlokasiBon(bon);
    }, [sumSetor, potManual, open]);
    const sumBon = Object.values(alokasiBon).reduce((s, v) => s + parseLocaleNum(v || 0), 0);
    const sumKasbon = Object.values(alokasiKasbon).reduce((s, v) => s + parseLocaleNum(v || 0), 0);
    const hak = sumSetor - sumBon - sumKasbon;
    const sisa = hak - parseLocaleNum(pembayaran || 0);

    const buildPayload = () => {
        const alok_setor = Object.entries(alokasiSetor).filter(([_, v]) => v && v.pcs > 0).map(([k, v]) => {
            const [sid, idx] = k.split("|");
            return { setor_id: sid, item_index: parseLocaleNum(idx), jumlah_pcs: parseLocaleNum(v.pcs), nilai: parseLocaleNum(v.nilai) };
        });
        const alok_bon = Object.entries(alokasiBon).filter(([_, v]) => parseLocaleNum(v) > 0).map(([bid, nilai]) => {
            const b = open.bons.find((x) => x.id === bid);
            return { bon_id: bid, bon_jenis: b.bon_jenis, nilai_potongan: parseLocaleNum(nilai) };
        });
        const alok_kb = Object.entries(alokasiKasbon).filter(([_, v]) => parseLocaleNum(v) > 0).map(([kid, nilai]) => ({ kasbon_id: kid, nilai_alokasi: parseLocaleNum(nilai) }));
        return {
            vendor_id: vendor.id, tanggal,
            alokasi_setor: alok_setor, alokasi_potongan_bon: alok_bon, alokasi_kasbon: alok_kb,
            petugas,
            periode_dari: dari, periode_sampai: sampai,
            tanda_tangan: { dibuat_oleh: ttDibuat, diperiksa_oleh: ttDiperiksa, disetujui_oleh: ttDisetujui },
        };
    };

    const openPreviewDraft = () => {
        setPreview({
            title: `Totalan Draft — ${vendor.nama}`,
            method: "POST",
            url: "/totalan/preview-draft",
            body: buildPayload(),
            draft: true,
            filename: "Totalan_Draft.pdf",
        });
    };

    const submitForm = async () => {
        const payload = buildPayload();
        if (payload.alokasi_setor.length === 0) return toast.error("Pilih minimal 1 alokasi setor");
        setSubmit(true);
        try {
            // v1.8.13 — Totalan langsung confirmed di backend (no draft state)
            const { data } = await api.post("/totalan", payload);
            toast.success(`Totalan ${data.nomor_totalan} berhasil dibuat`);
            if (parseLocaleNum(pembayaran) > 0) {
                await api.post(`/totalan/${data.id}/pembayaran`, {
                    jumlah: parseLocaleNum(pembayaran), tanggal, petugas,
                    idempotency_key: crypto.randomUUID(),
                });
                toast.success(`Pembayaran ${formatRupiah(pembayaran)} dicatat`);
            }
            onSaved();
        } catch (e) { console.error(e?.response?.status, e?.response?.data); toast.error(`Gagal: ${errMsg(e)}`, { duration: 8000 }); }
        finally { setSubmit(false); }
    };

    // v1.8.13 — Auto alokasi: Kasbon → Rijek B → Bon Kain/Peralatan FIFO
    const [autoLoading, setAutoLoading] = useState(false);
    const hitungOtomatis = async () => {
        setAutoLoading(true);
        try {
            const { data } = await api.get(`/vendors/${vendor.id}/totalan/auto-allocate`, {
                params: { tanggal_dari: dari, tanggal_sampai: sampai },
            });
            const newSetor = {};
            for (const a of (data.alokasi_setor || [])) {
                newSetor[`${a.setor_id}|${a.item_index}`] = { pcs: a.jumlah_pcs, nilai: a.nilai };
            }
            const newBon = {};
            for (const p of (data.alokasi_potongan_bon || [])) newBon[p.bon_id] = p.nilai_potongan;
            const newKas = {};
            for (const k of (data.alokasi_kasbon || [])) newKas[k.kasbon_id] = k.nilai_alokasi;
            setPotManual(false);
            setAlokasiSetor(newSetor);
            setAlokasiBon(newBon);
            setAlokasiKasbon(newKas);
            setPembayaran(data.hak_vendor || 0);
            const msg = `Alokasi otomatis: ${(data.alokasi_setor || []).length} setor · ${(data.alokasi_kasbon || []).length} kasbon · ${(data.alokasi_potongan_bon || []).length} bon · Hak Vendor ${formatRupiah(data.hak_vendor)}`;
            toast.success(msg, { duration: 5000 });
            if ((data.outstanding_bon || []).length > 0 || (data.outstanding_kasbon || []).length > 0) {
                toast.info(`${(data.outstanding_bon || []).length + (data.outstanding_kasbon || []).length} bon/kasbon belum lunas (akan diselesaikan periode berikut)`, { duration: 7000 });
            }
        } catch (e) {
            console.error(e?.response?.status, e?.response?.data);
            toast.error(`Gagal hitung otomatis: ${errMsg(e)}`, { duration: 8000 });
        } finally { setAutoLoading(false); }
    };

    return (
        <Modal title="Totalan" onClose={onClose} maxW="max-w-4xl" testid="totalan-modal">
            <div className="grid grid-cols-3 gap-3 mb-3">
                <Field label="Tanggal Setor Dari"><input type="date" value={dari} onChange={(e) => setDari(e.target.value)} className={inputCls} /></Field>
                <Field label="Tanggal Setor Sampai"><input type="date" value={sampai} onChange={(e) => setSampai(e.target.value)} className={inputCls} /></Field>
                <Field label="Tanggal Totalan"><input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} /></Field>
            </div>

            {/* v1.8.13 — Auto Alokasi: Kasbon → Rijek B → Bon Kain/Peralatan FIFO */}
            <div className="rounded-lg p-3 mb-3 flex items-center gap-3 flex-wrap" style={{ background: "var(--apple-blue-soft)", border: "1px solid var(--apple-blue-border)" }}>
                <div className="flex-1 text-xs" style={{ color: "var(--text-secondary)" }}>
                    <b style={{ color: "var(--text-primary)" }}>Alokasi Otomatis</b> — pilih rentang tanggal, klik tombol → setoran + potongan kasbon + bon diisi otomatis (urutan: Kasbon → Rijek B → Bon Kain/Peralatan FIFO).
                </div>
                <button
                    type="button"
                    onClick={hitungOtomatis}
                    disabled={autoLoading}
                    data-testid="totalan-auto-allocate"
                    className="px-3 py-1.5 rounded-lg font-medium text-xs text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                    style={{ background: "var(--apple-blue)" }}
                >
                    {autoLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                    Hitung Otomatis
                </button>
            </div>

            <div className="panel mb-3 p-3">
                <h3 className="font-bold text-xs text-slate-700 mb-2">Setoran Terkait (dalam rentang)</h3>
                <table className="table-nota text-xs">
                    <thead><tr><th>✓</th><th>Nomor</th><th>Produk</th><th className="text-right">Sisa Pcs</th><th className="text-right">Alokasi Pcs</th><th className="text-right">Nilai</th></tr></thead>
                    <tbody>
                        {setorsFiltered.map((it) => {
                            const key = `${it.setor_id}|${it.item_index}`;
                            const sisa = it.sisa_pcs;
                            const harga = it.harga_per_pcs || 0;
                            const checked = !!alokasiSetor[key];
                            const pcs = alokasiSetor[key]?.pcs || 0;
                            return (
                                <tr key={key}>
                                    <td><input type="checkbox" checked={checked} onChange={(e) => setAlokasiSetor((o) => ({ ...o, [key]: e.target.checked ? { pcs: sisa, nilai: sisa * harga } : undefined }))} data-testid={`tot-setor-chk-${key}`} /></td>
                                    <td className="font-mono">{it.nomor_setor}</td>
                                    <td>{it.nama_produk}</td>
                                    <td className="num">{sisa}</td>
                                    <td className="num"><input type="text" inputMode="decimal" value={pcs} onChange={(e) => { const p = Math.min(parseLocaleNum(e.target.value), sisa); setAlokasiSetor((o) => ({ ...o, [key]: p > 0 ? { pcs: p, nilai: p * harga } : undefined })); }} data-testid={`tot-setor-pcs-${key}`} className="w-16 border rounded px-1 text-right" /></td>
                                    <td className="num">{formatRupiah(pcs * harga)}</td>
                                </tr>
                            );
                        })}
                        {setorsFiltered.length === 0 && <tr><td colSpan={6} className="text-center py-3 italic text-slate-400" data-testid="tot-setor-empty">Tidak ada setoran tersisa dalam rentang</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="panel mb-3 p-3">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-xs text-slate-700">Alokasi Potongan Bon</h3>
                    <span className="text-[11px] text-slate-500" data-testid="tot-pot-mode">
                        {potManual ? "Diubah manual" : "Otomatis (Kasbon → Rijek B → Bon FIFO)"}
                        {potManual && (
                            <button type="button" onClick={() => setPotManual(false)} data-testid="tot-pot-reset" className="ml-2 px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-100 text-slate-700">Reset ke otomatis</button>
                        )}
                    </span>
                </div>
                <table className="table-nota text-xs">
                    <thead><tr><th>Nomor</th><th>Jenis</th><th className="text-right">Sisa Bon</th><th className="text-right">Potong Rp</th></tr></thead>
                    <tbody>
                        {open.bons.map((b) => (
                            <tr key={b.id}>
                                <td className="font-mono">{b.nomor}</td>
                                <td>{b.label}</td>
                                <td className="num">{formatRupiah(b.sisa)}</td>
                                <td className="num"><input type="text" inputMode="decimal" value={alokasiBon[b.id] || 0} onChange={(e) => { setPotManual(true); setAlokasiBon((o) => ({ ...o, [b.id]: Math.min(parseLocaleNum(e.target.value), b.sisa) })); }} data-testid={`tot-bon-${b.id}`} className="w-28 border rounded px-1 text-right" /></td>
                            </tr>
                        ))}
                        {open.bons.length === 0 && <tr><td colSpan={4} className="text-center py-3 italic text-slate-400">Tidak ada bon tersisa</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="panel mb-3 p-3">
                <h3 className="font-bold text-xs text-slate-700 mb-2">Alokasi Kasbon (dilunasi)</h3>
                <table className="table-nota text-xs">
                    <thead><tr><th>Nomor</th><th className="text-right">Sisa</th><th className="text-right">Alokasi Rp</th></tr></thead>
                    <tbody>
                        {open.kasbons.map((k) => (
                            <tr key={k.id}>
                                <td className="font-mono">{k.nomor}</td>
                                <td className="num">{formatRupiah(k.sisa)}</td>
                                <td className="num"><input type="text" inputMode="decimal" value={alokasiKasbon[k.id] || 0} onChange={(e) => { setPotManual(true); setAlokasiKasbon((o) => ({ ...o, [k.id]: Math.min(parseLocaleNum(e.target.value), k.sisa) })); }} data-testid={`tot-kas-${k.id}`} className="w-28 border rounded px-1 text-right" /></td>
                            </tr>
                        ))}
                        {open.kasbons.length === 0 && <tr><td colSpan={3} className="text-center py-3 italic text-slate-400">Tidak ada kasbon tersisa</td></tr>}
                    </tbody>
                </table>
            </div>

            <div className="panel p-3 bg-slate-50 mb-3 text-sm space-y-1" data-testid="totalan-preview">
                <Row label="Total Setor Dipilih" v={sumSetor} />
                <Row label="Potongan Bon" v={-sumBon} />
                <Row label="Alokasi Kasbon" v={-sumKasbon} />
                <Row label="Hak Vendor" v={hak} bold />
                <div className="pt-1 border-t border-slate-300 mt-1">
                    <Field label="Pembayaran Dicatat (opsional)">
                        <input type="text" inputMode="decimal" value={pembayaran} onChange={(e) => setPembayaran(e.target.value)} className={inputCls} data-testid="totalan-pembayaran" placeholder="Simpan draft dulu, bayar bertahap kemudian jika dikosongkan" />
                    </Field>
                </div>
                <Row label="Sisa Hak Belum Dibayar" v={sisa} bold />
            </div>

            <div className="panel p-3 mb-3">
                <h3 className="font-bold text-xs text-slate-700 mb-2">Validasi Tanda Tangan</h3>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Dibuat oleh">
                        <input value={ttDibuat} onChange={(e) => setTtDibuat(e.target.value)} className={inputCls} data-testid="tt-dibuat" />
                    </Field>
                    <Field label="Diperiksa oleh">
                        <input value={ttDiperiksa} onChange={(e) => setTtDiperiksa(e.target.value)} className={inputCls} data-testid="tt-diperiksa" />
                    </Field>
                    <Field label="Disetujui oleh">
                        <input value={ttDisetujui} onChange={(e) => setTtDisetujui(e.target.value)} className={inputCls} data-testid="tt-disetujui" />
                    </Field>
                </div>
            </div>

            <div className="flex justify-end gap-2">
                <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded text-sm">Batal</button>
                <button onClick={openPreviewDraft} data-testid="totalan-preview-a4" className="px-4 py-2 border border-teal-600 text-teal-700 hover:bg-teal-50 rounded font-medium text-sm inline-flex items-center gap-1.5">
                    <Printer className="w-3.5 h-3.5" /> Preview A4
                </button>
                <button disabled={submit} onClick={submitForm} data-testid="totalan-submit" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium text-sm">{parseLocaleNum(pembayaran) > 0 ? "Simpan Totalan + Catat Pembayaran" : "Simpan Totalan"}</button>
            </div>
            <PreviewNotaModal open={!!preview} onClose={() => setPreview(null)}
                title={preview?.title || ""} url={preview?.url || ""}
                filename={preview?.filename || "nota.pdf"}
                method={preview?.method || "GET"} body={preview?.body || null}
                draft={!!preview?.draft} />
        </Modal>
    );
}

function Row({ label, v, bold }) {
    return <div className={`flex justify-between ${bold ? "font-bold text-slate-900" : "text-slate-600"}`}><span>{label}</span><span className="num-tabular">{formatRupiah(v)}</span></div>;
}

// ===========================================================================
// Detail Transaksi Modal — full rincian + Cetak Nota
// ===========================================================================

const JENIS_TO_API = {
    "Pengambilan Kain": "bon_kain", "Bon Rijek B": "bon_kain", "Bon Peralatan": "bon_peralatan",
    "Setor Barang Jadi": "setor", "Kasbon": "kasbon",
    "Reject A (koreksi)": "reject", "Setor Perbaikan": "setor_perbaikan",
    "Pembayaran Totalan": "totalan",
};

const JENIS_TO_COLL = {
    bon_kain: "/bon-kain/", bon_peralatan: "/bon-peralatan/",
    setor: "/setor-barang/", kasbon: "/kasbon/",
    reject: "/reject?", setor_perbaikan: "/setor-perbaikan?",
    totalan: "/totalan/",
};

export function DetailTxModal({ entry, vendor, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const notaJenis = JENIS_TO_API[entry.jenis] || null;

    useEffect(() => {
        if (!notaJenis || !entry.ref_id) { setLoading(false); return; }
        setLoading(true);
        let url;
        if (notaJenis === "bon_kain") url = `/bon-kain/${entry.ref_id}`;
        else if (notaJenis === "bon_peralatan") url = `/bon-peralatan/${entry.ref_id}`;
        else if (notaJenis === "setor") url = `/setor-barang/${entry.ref_id}`;
        else if (notaJenis === "kasbon") url = `/kasbon/${entry.ref_id}`;
        else if (notaJenis === "totalan") url = `/totalan/${entry.ref_id}`;
        else if (notaJenis === "reject") url = `/reject/${entry.ref_id}`;
        else if (notaJenis === "setor_perbaikan") url = `/setor-perbaikan?vendor_id=${vendor?.id}`;
        api.get(url).then((r) => {
            let d = r.data;
            if (Array.isArray(d)) d = d.find((x) => x.id === entry.ref_id);
            setDetail(d);
        }).catch(() => setDetail(null)).finally(() => setLoading(false));
    }, [entry.ref_id, notaJenis, vendor?.id]);

    const openPdf = () => {
        setPreview({
            title: `${entry.jenis} ${entry.nomor}`,
            url: `/nota/${notaJenis}/${entry.ref_id}/pdf`,
            filename: `Nota_${notaJenis}_${entry.nomor || entry.ref_id.slice(0, 8)}.pdf`,
            share: {
                jenis: notaJenis, id: entry.ref_id,
                vendor_no_hp: vendor?.no_hp || "",
                vendor_nama: vendor?.nama || "",
                nota_kode: entry.nomor || entry.ref_id.slice(0, 8),
            },
        });
    };

    const dmy = (iso) => {
        if (!iso) return "-";
        const parts = String(iso).slice(0, 10).split("-");
        return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : iso;
    };

    return (
        <Modal title={`${entry.jenis} — ${entry.nomor}`} onClose={onClose} maxW="max-w-2xl" testid="detail-tx-modal">
            {loading && <div className="text-center text-sm text-slate-400 py-6">Memuat detail…</div>}
            {!loading && (
                <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 panel p-3 bg-slate-50">
                        <KV k="Tanggal" v={dmy(detail?.tanggal || detail?.tanggal_setor || entry.tanggal)} />
                        <KV k="Nomor ID" v={<span className="font-mono">{entry.nomor}</span>} />
                        <KV k="Vendor" v={`${vendor?.nama || "-"} (${vendor?.kode_vendor || "-"})`} />
                        <KV k="Petugas" v={detail?.petugas || "MONIKA"} />
                        {detail?.catatan && <KV k="Catatan" v={detail.catatan} col2 />}
                    </div>

                    {/* Rincian per jenis */}
                    {notaJenis === "bon_kain" && (
                        <div className="panel overflow-x-auto">
                            <h4 className="p-2 text-xs font-bold text-slate-700 border-b border-[var(--border)]">Rincian Kain</h4>
                            <table className="table-nota">
                                <thead><tr><th>Jenis Kain</th><th className="text-right">Qty</th><th className="text-right">Harga</th><th className="text-right">Subtotal</th></tr></thead>
                                <tbody>
                                    {(detail?.items || []).map((it, i) => (
                                        <tr key={i}><td>{it.nama_kain_snapshot}</td><td className="num">{it.jumlah_roll || 1} roll × {it.yard_per_roll} {it.satuan_transaksi || "yard"}</td><td className="num">{formatRupiah(it.harga_per_yard)}</td><td className="num">{formatRupiah(it.subtotal || (it.jumlah_roll * it.yard_per_roll * it.harga_per_yard))}</td></tr>
                                    ))}
                                    <tr className="font-bold bg-slate-50"><td colSpan={3} className="text-right">TOTAL</td><td className="num">{formatRupiah(detail?.total_nilai)}</td></tr>
                                </tbody>
                            </table>
                            {(detail?.produk_ditugaskan || []).length > 0 && (
                                <div className="p-2 border-t border-[var(--border)] text-xs">
                                    <span className="font-bold">Produk Ditugaskan:</span> {detail.produk_ditugaskan.map((p) => p.nama_produk_snapshot).join(", ")}
                                </div>
                            )}
                        </div>
                    )}

                    {notaJenis === "bon_peralatan" && (
                        <div className="panel overflow-x-auto">
                            <h4 className="p-2 text-xs font-bold text-slate-700 border-b border-[var(--border)]">Rincian Peralatan</h4>
                            <table className="table-nota">
                                <thead><tr><th>Item</th><th className="text-right">Qty</th><th className="text-right">Harga</th><th className="text-right">Subtotal</th></tr></thead>
                                <tbody>
                                    {(detail?.items || []).map((it, i) => (
                                        <tr key={i}><td>{it.nama_item_snapshot}</td><td className="num">{it.qty}</td><td className="num">{formatRupiah(it.harga_snapshot)}</td><td className="num">{formatRupiah(it.subtotal || (it.qty * it.harga_snapshot))}</td></tr>
                                    ))}
                                    <tr className="font-bold bg-slate-50"><td colSpan={3} className="text-right">TOTAL</td><td className="num">{formatRupiah(detail?.total_nilai)}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    {notaJenis === "setor" && (
                        <div className="panel overflow-x-auto">
                            <h4 className="p-2 text-xs font-bold text-slate-700 border-b border-[var(--border)]">Alokasi Setor</h4>
                            <table className="table-nota">
                                <thead><tr><th>Produk</th><th>Ref Pengambilan</th><th className="text-right">Pcs</th><th className="text-right">Harga/Pcs</th><th className="text-right">Subtotal</th></tr></thead>
                                <tbody>
                                    {(detail?.items || []).map((it, i) => (
                                        <tr key={i}><td>{it.nama_produk_snapshot}</td><td className="font-mono text-xs">{(it.bon_kain_id || "").slice(0, 8)}</td><td className="num">{it.jumlah_pcs}</td><td className="num">{formatRupiah(it.harga_per_pcs_snapshot)}</td><td className="num">{formatRupiah(it.subtotal)}</td></tr>
                                    ))}
                                    <tr className="font-bold bg-slate-50"><td colSpan={4} className="text-right">TOTAL</td><td className="num">{formatRupiah(detail?.total_nilai)}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    {notaJenis === "kasbon" && detail && (
                        <div className="panel p-3 space-y-2">
                            <KV k="Jumlah" v={formatRupiah(detail.jumlah_kasbon)} />
                            <KV k="Alasan" v={detail.alasan || "-"} />
                            {(detail.alokasi_pengambilan || []).length > 0 && (
                                <div><div className="text-xs font-bold text-slate-700 mt-2">Alokasi Pengambilan:</div>
                                    <ul className="text-xs ml-4 list-disc">
                                        {detail.alokasi_pengambilan.map((a, i) => <li key={i}>Pengambilan <span className="font-mono">{a.bon_kain_id.slice(0, 8)}</span>: {formatRupiah(a.nominal)}</li>)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {notaJenis === "reject" && detail && (
                        <div className="panel p-3 space-y-2">
                            <KV k="Jenis Reject" v={detail.jenis === "A_bisa_diperbaiki" ? "A — Bisa Diperbaiki" : "B — Buyback"} />
                            <KV k="Produk" v={detail.nama_produk_snapshot} />
                            <KV k={detail.satuan === "meter" ? "Jumlah Meter" : "Jumlah Pcs"} v={`${fmtQty(detail.jumlah_pcs)} ${detail.satuan === "meter" ? "mtr" : "pcs"}`} />
                            {detail.jenis === "B_tidak_bisa_diperbaiki" && <KV k={detail.satuan === "meter" ? "Harga Manual/Meter" : "Harga Manual/Pcs"} v={formatRupiah(detail.harga_pcs_manual)} />}
                            <KV k="Alasan" v={detail.alasan || "-"} col2 />
                            <KV k="Ref Setor" v={<span className="font-mono text-xs">{detail.setor_nomor_snapshot}</span>} />
                            {detail.bon_baru_id && <KV k="Bon Baru" v={<span className="font-mono text-xs">{detail.bon_baru_id.slice(0, 8)}</span>} />}
                        </div>
                    )}

                    {notaJenis === "setor_perbaikan" && detail && (
                        <div className="panel p-3 space-y-2">
                            <KV k="Produk" v={detail.nama_produk_snapshot} />
                            <KV k="Jumlah Pcs" v={detail.jumlah_pcs} />
                            <KV k="Harga Master Snapshot" v={formatRupiah(detail.harga_pcs_master_snapshot)} />
                            <KV k="Nilai Kredit" v={<span className="text-teal-700 font-bold">{formatRupiah(detail.nilai_kredit)}</span>} />
                            <KV k="Ref Reject" v={<span className="font-mono text-xs">{detail.reject_nomor_snapshot}</span>} />
                        </div>
                    )}

                    {notaJenis === "totalan" && detail && (
                        <div className="panel p-3 space-y-2 text-sm">
                            <KV k="Sum Setor Dialokasi" v={formatRupiah(detail.sum_setor_dialokasi)} />
                            <KV k="Potongan Bon" v={`-${formatRupiah(detail.sum_potongan_bon)}`} />
                            <KV k="Alokasi Kasbon" v={`-${formatRupiah(detail.sum_alokasi_kasbon)}`} />
                            <KV k="Hak Vendor" v={<b className="text-slate-900">{formatRupiah(detail.hak_vendor)}</b>} />
                            <KV k="Status Konfirmasi" v={<span className={`badge ${detail.status_konfirmasi === "dikonfirmasi" ? "badge-hijau" : "badge-abu"}`}>{detail.status_konfirmasi}</span>} />
                            <KV k="Jumlah Pembayaran" v={`${(detail.pembayaran || []).length} transaksi`} />
                        </div>
                    )}

                    {notaJenis && entry.ref_id && (
                        <div className="flex justify-end">
                            <button onClick={openPdf} data-testid="detail-cetak-nota" className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded font-medium text-sm inline-flex items-center gap-1.5">
                                <Printer className="w-4 h-4" /> Cetak Nota PDF
                            </button>
                        </div>
                    )}
                </div>
            )}
            <PreviewNotaModal open={!!preview} onClose={() => setPreview(null)}
                title={preview?.title || ""} url={preview?.url || ""} filename={preview?.filename || "nota.pdf"}
                share={preview?.share || null} />
        </Modal>
    );
}

function KV({ k, v, col2 }) {
    return (
        <div className={col2 ? "col-span-2" : ""}>
            <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">{k}</div>
            <div className="text-sm text-slate-900">{v}</div>
        </div>
    );
}

// ===========================================================================
// v1.8.18 — Edit Transaksi (Bon Kain / Bon Peralatan / Setoran / Kasbon)
// ===========================================================================
const EDIT_CFG = {
    bon_kain: { label: "Pengambilan Kain", url: "/bon-kain/", dateKey: "tanggal", noteKey: "catatan" },
    bon_peralatan: { label: "Bahan Penolong", url: "/bon-peralatan/", dateKey: "tanggal", noteKey: "catatan" },
    setor: { label: "Setoran", url: "/setor-barang/", dateKey: "tanggal_setor", noteKey: "catatan" },
    kasbon: { label: "Kasbon", url: "/kasbon/", dateKey: "tanggal", noteKey: "alasan" },
};
const ITEM_FIELDS = {
    bon_kain: [["nama_kain_snapshot", "Nama Kain", "text"], ["jumlah_roll", "Roll", "num"], ["yard_per_roll", "Yard/Roll", "num"], ["harga_per_yard", "Harga/Yard", "num"]],
    bon_peralatan: [["nama_item_snapshot", "Nama Item", "text"], ["qty", "Qty", "num"], ["harga_snapshot", "Harga", "num"]],
    setor: [["nama_produk_snapshot", "Produk", "ro"], ["jumlah_pcs", "Jumlah", "num"], ["harga_per_pcs_snapshot", "Harga/Pcs", "num"]],
};
const toLocal = (n) => (n === null || n === undefined ? "" : String(n).replace(".", ","));
const lineTotal = (jenis, it) => {
    const p = parseLocaleNum;
    if (jenis === "bon_kain") return p(it.jumlah_roll) * p(it.yard_per_roll) * p(it.harga_per_yard);
    if (jenis === "bon_peralatan") return p(it.qty) * p(it.harga_snapshot);
    return p(it.jumlah_pcs) * p(it.harga_per_pcs_snapshot);
};

function EditTransaksiModal({ existing, onClose, onSaved }) {
    if (existing.jenis === "reject") return <RijekEditModal existing={existing} onClose={onClose} onSaved={onSaved} />;
    return <EditTransaksiForm existing={existing} onClose={onClose} onSaved={onSaved} />;
}

function EditTransaksiForm({ existing, onClose, onSaved }) {
    const cfg = EDIT_CFG[existing.jenis];
    const fields = ITEM_FIELDS[existing.jenis] || [];
    const [doc, setDoc] = useState(null);
    const [tanggal, setTanggal] = useState("");
    const [catatan, setCatatan] = useState("");
    const [items, setItems] = useState([]);
    const [jumlah, setJumlah] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`${cfg.url}${existing.id}`).then(({ data }) => {
            setDoc(data);
            setTanggal(data[cfg.dateKey] || "");
            setCatatan(data[cfg.noteKey] || "");
            setItems((data.items || []).map((it) => {
                const o = { ...it };
                fields.forEach(([k, , t]) => { if (t === "num") o[k] = toLocal(it[k]); });
                return o;
            }));
            setJumlah(toLocal(data.jumlah_kasbon));
        }).catch((e) => { toast.error(errMsg(e, "Gagal memuat transaksi")); onClose(); });
        // eslint-disable-next-line
    }, [existing.id]);

    const setItem = (i, k, v) => setItems((prev) => prev.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
    const total = existing.jenis === "kasbon" ? parseLocaleNum(jumlah) : items.reduce((s, it) => s + lineTotal(existing.jenis, it), 0);

    const submit = async () => {
        if (!tanggal) return toast.error("Tanggal wajib diisi");
        const payload = { [cfg.dateKey]: tanggal, [cfg.noteKey]: catatan };
        if (existing.jenis === "kasbon") {
            payload.jumlah_kasbon = parseLocaleNum(jumlah);
            if (payload.jumlah_kasbon <= 0) return toast.error("Jumlah kasbon harus > 0");
        } else {
            payload.items = items.map((it) => {
                const o = { ...it };
                fields.forEach(([k, , t]) => { if (t === "num") o[k] = parseLocaleNum(it[k]); });
                return o;
            });
        }
        setSaving(true);
        try {
            const { data: upd } = await api.put(`${cfg.url}${existing.id}`, payload);
            toast.success(`${existing.nomor || cfg.label} berhasil diperbarui`);
            if (upd?.warning) toast.warning(upd.warning, { duration: 8000 });
            onSaved();
        } catch (e) {
            toast.error(errMsg(e, "Gagal menyimpan perubahan"));
        } finally { setSaving(false); }
    };

    return (
        <Modal title={`Edit ${cfg.label}: ${existing.nomor || ""}`} onClose={onClose} testid="edit-tx-modal">
            {!doc && <div className="py-8 text-center text-sm text-slate-400">Memuat transaksi…</div>}
            {doc && (
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Jenis Transaksi">
                            <select disabled value={existing.jenis} data-testid="edit-jenis" className={`${inputCls} bg-slate-50 text-slate-500`}>
                                <option value={existing.jenis}>{cfg.label}</option>
                            </select>
                        </Field>
                        <Field label="Tanggal" required>
                            <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} data-testid="edit-tanggal" className={inputCls} />
                        </Field>
                    </div>
                    {existing.jenis === "kasbon" ? (
                        <Field label="Jumlah Kasbon" required>
                            <input type="text" inputMode="decimal" value={jumlah} onChange={(e) => setJumlah(e.target.value)} data-testid="edit-jumlah-kasbon" className={inputCls} />
                        </Field>
                    ) : (
                        <div className="space-y-2" data-testid="edit-items">
                            {items.map((it, i) => (
                                <div key={i} className="panel p-2 grid gap-2" style={{ gridTemplateColumns: `repeat(${fields.length}, minmax(0,1fr))` }}>
                                    {fields.map(([k, l, t]) => (
                                        <Field key={k} label={l}>
                                            <input
                                                type="text" inputMode={t === "num" ? "decimal" : "text"} value={it[k] ?? ""} readOnly={t === "ro"}
                                                onChange={(e) => setItem(i, k, e.target.value)} data-testid={`edit-item-${i}-${k}`}
                                                className={`${inputCls} ${t === "ro" ? "bg-slate-50 text-slate-500" : ""}`} />
                                        </Field>
                                    ))}
                                    <div className="text-[11px] text-slate-500 text-right" style={{ gridColumn: "1 / -1" }}>Subtotal: <b>{formatRupiah(lineTotal(existing.jenis, it))}</b></div>
                                </div>
                            ))}
                        </div>
                    )}
                    <Field label={existing.jenis === "kasbon" ? "Alasan" : "Catatan"}>
                        <textarea value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="edit-catatan" className={inputCls} rows={2} />
                    </Field>
                    <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
                        <div className="text-sm">Total: <b data-testid="edit-total">{formatRupiah(total)}</b></div>
                        <div className="flex gap-2">
                            <button onClick={onClose} data-testid="edit-cancel" className="px-3 py-1.5 border border-slate-300 rounded text-sm">Batal</button>
                            <button onClick={submit} disabled={saving} data-testid="edit-submit" className="px-4 py-1.5 rounded text-sm font-medium text-white bg-[#0071E3] hover:bg-[#0077ED] disabled:opacity-50 inline-flex items-center gap-1.5">
                                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Simpan Perubahan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
}
